import * as Yup from "yup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { merge } from "lodash";
import { isBefore } from "date-fns";
import { Icon } from "@iconify/react";
import { useSnackbar } from "notistack5";
import trash2Fill from "@iconify/icons-eva/trash-2-fill";
import { useFormik, Form, FormikProvider } from "formik";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Stack,
  Button,
  Select,
  Tooltip,
  MenuItem,
  TextField,
  InputLabel,
  IconButton,
  FormControl,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import { LoadingButton, MobileDatePicker } from "@material-ui/lab";
import ColorSinglePicker from "../../ColorSinglePicker";
import { TimeSelect } from "components/TimeSelect";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import {
  CALENDAR_CLIENT_NOTE_MAX_LENGTH,
  calendarEventStatuses,
  calendarEventTypes
} from "../../../@types/sharedSchema";
import { NewMultipleContactPicker } from "../listingsMatch/NewContactPicker";
import {
  hasClientNote,
  getTaskStartEndDate,
  getMeetingStartEndDate,
  resolveClientNoteWrite,
  normalizeClientNoteInput,
  parseAttendeesToContacts,
  parseContactsToAttendees,
  shouldShowClientNoteInput
} from "utils/calendar/helper";
import { CheckedMenuItemLabel } from "components/__haim/Common/CheckedMenuItemLabel";
import { CalendarFormTimeStatus, getTimeStatusDateByTimeStatus } from "./CalendarFormTimeStatus";
import { CalendarCustomerCard } from "./CalendarCustomerCard";
import { NewListingPicker } from "../signatures/NewListingPicker";
import { CalendarListingCard } from "./CalendarListingCard";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import useWebotServer from "hooks/useWebotServer";
import useCalendarClientNoteEnabled from "hooks/useCalendarClientNoteEnabled";
import useOrgDirectory from "hooks/useOrgDirectory";
import { normalizeUserEmail, taskOwnerOf } from "utils/calendar/officeTasks";
import { CalendarTaskAssigneeSelect, useHasOfficeColleagues } from "./CalendarTaskAssigneeSelect";
import type { KeyboardEvent } from "react";
import type { TimeStatus } from "./CalendarFormTimeStatus";
import type { CalendarContextProps } from "../../../@types/calendar";
import type {
  CalendarEvent,
  CalendarEventType,
  ProcessedListingData,
  REST_schema
} from "../../../@types/sharedSchema";

// ----------------------------------------------------------------------

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

type ConflictWarning = {
  contactName: string;
  eventType: "meeting" | "task";
  realtorName: string;
  date: Date;
};

type PastMeetingRealtor = {
  contactName: string;
  realtorName: string;
  meetingsCount: number;
  lastMeetingDate: Date;
};

function getConflictsFromTimeline(
  timeline: REST_schema.ContactTimelineAction[],
  eventStartDate: Date,
  currentUserMail: string,
  contactName: string
): ConflictWarning[] {
  const eventTime = eventStartDate.getTime();

  return timeline
    .filter((action) => {
      if (action.actionType !== "meeting" && action.actionType !== "task") return false;
      if (!action.actionTime) return false;
      if (action.actionUser?.userMail === currentUserMail) return false;

      const actionTime = new Date(action.actionTime).getTime();
      return Math.abs(actionTime - eventTime) <= TWO_WEEKS_MS;
    })
    .map((action) => ({
      contactName,
      eventType: action.actionType as "meeting" | "task",
      realtorName: action.actionUser?.userName ?? action.actionUser?.userMail ?? "",
      date: new Date(action.actionTime!)
    }));
}

function getPastMeetingRealtorsFromTimeline(
  timeline: REST_schema.ContactTimelineAction[],
  currentUserMail: string,
  contactName: string
): PastMeetingRealtor[] {
  const now = Date.now();
  const byRealtor = new Map<string, PastMeetingRealtor>();

  for (const action of timeline) {
    if (action.actionType !== "meeting") continue;
    if (!action.actionTime) continue;
    if (action.actionUser?.userMail === currentUserMail) continue;

    const actionDate = new Date(action.actionTime);
    if (actionDate.getTime() > now) continue;

    const realtorKey = action.actionUser?.userMail ?? action.actionUser?.userName ?? "";
    if (!realtorKey) continue;

    const realtorName = action.actionUser?.userName ?? action.actionUser?.userMail ?? "";
    const existing = byRealtor.get(realtorKey);
    if (existing) {
      existing.meetingsCount += 1;
      if (actionDate.getTime() > existing.lastMeetingDate.getTime()) {
        existing.lastMeetingDate = actionDate;
      }
    } else {
      byRealtor.set(realtorKey, {
        contactName,
        realtorName,
        meetingsCount: 1,
        lastMeetingDate: actionDate
      });
    }
  }

  return Array.from(byRealtor.values()).sort(
    (a, b) => b.lastMeetingDate.getTime() - a.lastMeetingDate.getTime()
  );
}

// ----------------------------------------------------------------------

const COLOR_OPTIONS = [
  "#00AB55", // theme.palette.primary.main,
  "#1890FF", // theme.palette.info.main,
  "#94D82D", // theme.palette.success.main,
  "#FFC107", // theme.palette.warning.main,
  "#FF4842", // theme.palette.error.main
  "#04297A", // theme.palette.info.darker
  "#7A0C2E" // theme.palette.error.darker
];

const getInitialValues = (event: CalendarEvent | null): CalendarEvent => {
  const { start, end } = getMeetingStartEndDate();

  const _event = {
    title: "",
    description: "",
    // Controlled from the first render; an existing event's note is merged over it below.
    clientNote: "",
    textColor: "#1890FF",
    allDay: false,
    start: (event?.start ?? start) as Date,
    end: (event?.end ?? end) as Date,
    attendees: [],
    listingIds: [],
    listingsData: [],
    eventType: event?.eventType ?? "meeting"
  };

  if (event) {
    return merge({}, _event, event);
  }

  return _event;
};

// ----------------------------------------------------------------------

type CalendarFormProps = {
  event: CalendarEvent;
  onCancel: VoidFunction;
  onUpdate: CalendarContextProps["createOrUpdateEvent"];
  onDelete: CalendarContextProps["deleteEvent"];
};

function setInitialTaskStartTime(event: CalendarEvent | null): string {
  if (!event?.id) return "";

  const taskStartTime = moment(event.start).format("HH:mm");

  if (taskStartTime === "00:00") return "";

  return taskStartTime;
}

function setInitialSelectedListings(event: CalendarEvent | null): ProcessedListingData[] {
  return event?.listingsData ?? [];
}

/**
 * Stable identity for a set of attendees — sorted phone numbers (or ids),
 * joined. Used to tell whether the attendee set changed since the form opened,
 * so an edit that leaves attendees untouched skips the org conflict check.
 */
function buildAttendeesKey(attendees?: any[]): string {
  return (
    attendees
      ?.map((attendee: any) => attendee.phoneNumberInternational ?? attendee.id)
      .filter(Boolean)
      .sort()
      .join(",") ?? ""
  );
}

export default function CalendarForm({ event, onUpdate, onDelete, onCancel }: CalendarFormProps) {
  const [selectedListings, setSelectedListings] = useState<ProcessedListingData[]>(() =>
    setInitialSelectedListings(event)
  );

  const [taskStartTime, setTaskStartTime] = useState(() => setInitialTaskStartTime(event));
  const [conflictWarnings, setConflictWarnings] = useState<ConflictWarning[]>([]);
  const [pastMeetingRealtors, setPastMeetingRealtors] = useState<PastMeetingRealtor[]>([]);
  const [conflictCheckFailed, setConflictCheckFailed] = useState(false);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const { t: translate } = useTranslation();
  const { user } = useAuth();
  const { getContactDetails } = useWebotServer();
  const { organization } = useFirestore();

  // The "message to invitees" input (`clientNote`). The gate only decides whether to OFFER
  // it on an event that has no note; a note the event was opened with is always shown.
  const clientNoteGate = useCalendarClientNoteEnabled();
  // Read once, like Formik's initial values: this is about the event AS IT WAS OPENED, so
  // clearing the text (or the dialog dropping `event` while it closes) must not hide the input.
  const [hasStoredClientNote] = useState(() => hasClientNote(event?.clientNote));
  // Was the input on screen at any point in this form session? Decides what a save does to
  // the stored note: see `resolveClientNoteWrite`.
  const clientNoteInputWasShownRef = useRef(false);

  const isCreating = !event?.id;

  // Office tasks: whose calendar this task is in, and whose it should be in after the save.
  // A task a colleague assigned to the user is the user's; one the user assigned is the
  // colleague's, and only its time, text and status can change here (never into a meeting).
  const me = normalizeUserEmail(user?.email);
  const ownerId = taskOwnerOf(event, me);
  const isOwnEvent = ownerId === me;
  const hasOfficeColleagues = useHasOfficeColleagues();
  const { getMember } = useOrgDirectory();
  const [assigneeUserId, setAssigneeUserId] = useState(ownerId);
  const assignedByName = event?.assignedByUserId
    ? getMember(event.assignedByUserId)?.displayName ?? event.assignedByUserId
    : null;

  const EventSchema = Yup.object().shape({
    title: Yup.string().max(255).required("Title is required"),
    description: Yup.string().max(5000),
    clientNote: Yup.string().max(CALENDAR_CLIENT_NOTE_MAX_LENGTH)
  });

  const formik = useFormik({
    initialValues: getInitialValues(event),
    validationSchema: EventSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      const parsedListingIds = selectedListings.map(({ id }) => id);
      const parsedListingsData = selectedListings;

      const parsedContactsToAttendees = parseContactsToAttendees(values.attendees);

      const newEvent: CalendarEvent = {
        id: event.id,
        title: values.title,
        description: values.description,
        backgroundColor: values.textColor,
        textColor: values.textColor,
        allDay: values.allDay,
        start: values.start,
        end: values.end,
        attendees: parsedContactsToAttendees,
        listingIds: parsedListingIds,
        listingsData: parsedListingsData,
        eventType: values.eventType as CalendarEventType
      };

      // `clientNote` is the one field here that is not always written: never shown in this
      // session means the key stays out and a stored note survives; shown means the typed
      // text, or "" once the event can no longer message anyone (a task, nobody reachable).
      const clientNoteWrite = resolveClientNoteWrite({
        inputWasShown: clientNoteInputWasShownRef.current,
        eventType: newEvent.eventType,
        attendees: parsedContactsToAttendees,
        value: values.clientNote
      });

      if (clientNoteWrite.write) {
        newEvent.clientNote = clientNoteWrite.value;
      }

      if (isTaskEvent) {
        newEvent.status = values.status ?? "pending";

        if (taskStartTime) {
          const [hours, minutes] = taskStartTime.split(":").map(Number);

          const newStartDate = moment(values.start)
            .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
            .toDate();

          const { start, end } = getMeetingStartEndDate(newStartDate);

          newEvent.allDay = false;

          newEvent.start = start;
          newEvent.end = end;
        } else {
          const { start, end } = getTaskStartEndDate(values.start);

          newEvent.allDay = true;

          newEvent.start = start;
          newEvent.end = end;
        }
      } else {
        newEvent.status = null;
      }

      // A task whose owner or assignee is not the user crosses calendars: the server does the
      // write, and the calendar context reports the outcome itself.
      const isOfficeTaskSave = isTaskEvent && (!isOwnEvent || assigneeUserId !== me);

      onUpdate(newEvent, isTaskEvent ? { ownerUserId: ownerId, assigneeUserId } : undefined)
        .then((success) => {
          if (isOfficeTaskSave) return;
          if (success) {
            const message =
              event.eventType === "task"
                ? translate("calendarPage.form.taskUpdatedSuccesfully")
                : translate("calendarPage.form.eventUpdatedSuccesfully");
            enqueueSnackbar(message, {
              variant: "success"
            });
          } else {
            const message =
              event.eventType === "task"
                ? translate("calendarPage.form.taskOperationFailure")
                : translate("calendarPage.form.eventOperationFailure");
            enqueueSnackbar(message, {
              variant: "error"
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });

      onCancel();
      resetForm();
      setSubmitting(false);
    }
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    submitForm,
    handleSubmit,
    getFieldProps,
    setFieldValue
  } = formik;

  const eventType = values.eventType;

  useEffect(() => {
    if (!isCreating) return;

    const hasPresetRange = event?.start && event?.end;

    if (eventType === "meeting") {
      if (hasPresetRange) {
        setFieldValue("start", event.start);
        setFieldValue("end", event.end);
      } else {
        const { start, end } = getMeetingStartEndDate();
        setFieldValue("start", start);
        setFieldValue("end", end);
      }
    } else {
      const { start, end } = getTaskStartEndDate(hasPresetRange ? event.start : values.start);

      setFieldValue("start", start);
      setFieldValue("end", end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreating, eventType]);

  const attendeesKey = useMemo(() => buildAttendeesKey(values.attendees), [values.attendees]);

  // The attendee set the event already had when the form opened. For an edit
  // whose attendees are untouched, there is nothing new to check against the
  // org — so we must not run the (Save-blocking) conflict check.
  const originalAttendeesKey = useMemo(() => buildAttendeesKey(event?.attendees), [event]);
  const attendeesChanged = attendeesKey !== originalAttendeesKey;

  const eventStartDateKey = values.start?.toDateString();

  const fetchConflicts = useCallback(async () => {
    console.log("Fetching conflicts for attendees:", attendeesKey, "at date:", eventStartDateKey);
    if (!attendeesKey || !organization) {
      setConflictWarnings([]);
      setPastMeetingRealtors([]);
      setConflictCheckFailed(false);
      setIsCheckingConflicts(false);
      return;
    }

    setIsCheckingConflicts(true);
    const currentUserMail = user?.email ?? "";
    const phoneNumbers = attendeesKey.split(",").filter(Boolean);
    const allWarnings: ConflictWarning[] = [];
    const allPastRealtors: PastMeetingRealtor[] = [];
    let hasFailed = false;

    for (const phone of phoneNumbers) {
      try {
        const response: REST_schema.ContactDetailsResponseSchema = await getContactDetails(phone, [
          "contacts",
          "calendarEvents"
        ]);

        console.log("Contact details response for phone", phone, ":", response);
        if (!response?.length) continue;

        const { timeline } = response[0];
        const fullName = response[0].displayName || phone;
        const contactName = fullName.split(" ")[0] || fullName;

        const conflicts = getConflictsFromTimeline(
          timeline,
          values.start,
          currentUserMail,
          contactName
        );
        console.log("Conflicts for contact", contactName, ":", conflicts);
        allWarnings.push(...conflicts);

        const pastRealtors = getPastMeetingRealtorsFromTimeline(
          timeline,
          currentUserMail,
          contactName
        );
        console.debug("Past meeting realtors for contact", contactName, ":", pastRealtors);
        allPastRealtors.push(...pastRealtors);
      } catch (error) {
        console.error("Failed to fetch contact details for conflict check:", error);
        hasFailed = true;
      }
    }

    setConflictWarnings(allWarnings);
    setPastMeetingRealtors(allPastRealtors);
    setConflictCheckFailed(hasFailed);
    setIsCheckingConflicts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendeesKey, eventStartDateKey]);

  useEffect(() => {
    // Only run the org-wide conflict check when it can matter: a brand-new
    // event, or an edit whose attendees actually changed. Editing an existing
    // event without touching attendees (e.g. changing the colour or title)
    // must not block Save while it re-checks something that hasn't changed.
    if (isCreating || attendeesChanged) {
      fetchConflicts();
      return;
    }
    setConflictWarnings([]);
    setPastMeetingRealtors([]);
    setConflictCheckFailed(false);
    setIsCheckingConflicts(false);
  }, [fetchConflicts, isCreating, attendeesChanged]);

  const handleDelete = async () => {
    if (!event.id) return;
    try {
      onCancel();
      // A task the user assigned is cancelled through the server, which reports the outcome.
      onDelete(event.id, ownerId);
      if (!isOwnEvent) return;
      enqueueSnackbar("Delete event success", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Delete event failed", { variant: "error" });
    }
  };

  const handleChangeTaskTimeStatus = (timeStatus: TimeStatus) => (): void => {
    const newStartDate = getTimeStatusDateByTimeStatus(timeStatus);

    const { start, end } = getTaskStartEndDate(newStartDate);

    setFieldValue("end", end);
    setFieldValue("start", start);
  };

  const handleTitleSubmitOnEnter = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== "Enter") return;

    if (!e.repeat) {
      const formTarget = e.target as EventTarget & { form: HTMLFormElement };
      formTarget.form.requestSubmit();
    }

    e.preventDefault();
  };

  const isDateError = isBefore(new Date(values.end), new Date(values.start));

  const parsedAttendeesToContacts = parseAttendeesToContacts(values.attendees);

  const isTaskEvent = values.eventType === "task";

  const showClientNote = useMemo(
    () =>
      shouldShowClientNoteInput({
        gate: clientNoteGate,
        hasStoredNote: hasStoredClientNote,
        eventType: values.eventType,
        attendees: values.attendees
      }),
    [clientNoteGate, hasStoredClientNote, values.eventType, values.attendees]
  );

  useEffect(() => {
    if (showClientNote) clientNoteInputWasShownRef.current = true;
  }, [showClientNote]);

  // One paragraph: the note is a single WhatsApp template parameter, which cannot hold a
  // line break. Enter does nothing here (it must not submit a message to clients either);
  // a pasted or mobile-keyboard newline is turned into a space in onChange.
  const handleClientNoteKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter") e.preventDefault();
  };

  const clientNoteLength = (values.clientNote ?? "").length;

  // The lab pickers' action buttons are component defaults ("OK" / "CANCEL" /
  // "TODAY"), not date-fns strings, so the locale now wired up in src/index.tsx
  // does not translate them — it only makes the calendar body Hebrew. Pin them
  // so the modal isn't half Hebrew and half English.
  const datePickerActionTexts = {
    okText: translate("general.confirm"),
    cancelText: translate("general.cancel"),
    todayText: translate("calendarPage.form.timeStatusLabel.today")
  };

  const dialogTitle = isCreating
    ? isTaskEvent
      ? translate("calendarPage.control.newTask")
      : translate("calendarPage.control.newMeeting")
    : isTaskEvent
    ? translate("calendarPage.control.editTask")
    : translate("calendarPage.control.editMeeting");

  return (
    <>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <FormikProvider value={formik}>
        <Form autoComplete="off" onSubmit={handleSubmit}>
          <DialogContent sx={{ py: 0, overflowY: "unset" }}>
            <Tabs
              centered
              sx={{ mb: 2 }}
              value={values.eventType}
              onChange={(_, newValue) => setFieldValue("eventType", newValue)}
            >
              {calendarEventTypes.map((item) => (
                <Tab
                  label={translate(`calendarPage.form.eventTypeLabel.${item}`)}
                  value={item}
                  key={item}
                  // A colleague's task stays a task: only a task can be assigned.
                  disabled={!isOwnEvent && item !== "task"}
                />
              ))}
            </Tabs>
            {isTaskEvent && isOwnEvent && assignedByName && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {translate("calendarPage.officeTasks.assignedByLine", { name: assignedByName })}
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              sx={{ mb: 3 }}
              label={translate("calendarPage.form.title")}
              error={Boolean(touched.title && errors.title)}
              helperText={touched.title && errors.title}
              onKeyDown={handleTitleSubmitOnEnter}
              {...getFieldProps("title")}
            />
            {isTaskEvent ? (
              <Stack sx={{ mb: 2 }} gap={4}>
                <CalendarFormTimeStatus
                  event={values}
                  onChangeTaskStartTimeStatus={handleChangeTaskTimeStatus}
                />
                <Stack direction="row" gap={2}>
                  <MobileDatePicker
                    label={translate("calendarPage.form.date")}
                    value={values.start}
                    minDate={new Date()}
                    disabled={isSubmitting}
                    onChange={(value) => {
                      const { start, end } = getTaskStartEndDate(value);

                      setFieldValue("start", start);
                      setFieldValue("end", end);
                    }}
                    inputFormat="dd/MM/yyyy"
                    {...datePickerActionTexts}
                    renderInput={(params) => (
                      <TextField {...params} sx={{ width: 112 }} variant="standard" />
                    )}
                  />
                  <TimeSelect
                    label={translate("calendarPage.form.time")}
                    value={taskStartTime}
                    // Unchanged contract: "" means "no specific hour", which
                    // onSubmit turns into an all-day task. Midnight has always
                    // been the way to express that, so it still maps to "".
                    onChange={(timeValue) =>
                      setTaskStartTime(timeValue === "00:00" ? "" : timeValue)
                    }
                    clearable
                    sx={{ width: 140 }}
                    variant="standard"
                  />
                </Stack>
                <FormControl fullWidth>
                  <InputLabel>{translate("calendarPage.form.status")}</InputLabel>
                  <Select
                    required
                    name="status"
                    value={values.status ?? "pending"}
                    label={translate("calendarPage.form.status")}
                    onChange={({ target: { value } }) => setFieldValue("status", value)}
                    renderValue={(selected) =>
                      translate(`calendarPage.form.statusLabel.${selected}`)
                    }
                  >
                    {calendarEventStatuses.map((item) => (
                      <MenuItem value={item} key={item}>
                        <CheckedMenuItemLabel selected={values.status === item}>
                          {translate(`calendarPage.form.statusLabel.${item}`)}
                        </CheckedMenuItemLabel>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(hasOfficeColleagues || !isOwnEvent) && (
                  <CalendarTaskAssigneeSelect
                    value={assigneeUserId}
                    onChange={setAssigneeUserId}
                    me={me}
                    ownerId={ownerId}
                    disabled={isSubmitting}
                  />
                )}
              </Stack>
            ) : (
              <>
                <Stack sx={{ mb: 2 }} direction="row" gap={2}>
                  <MobileDatePicker
                    label={translate("calendarPage.form.startDay")}
                    minDate={new Date()}
                    inputFormat="dd/MM/yyyy"
                    value={values.start}
                    onChange={(date) => {
                      setFieldValue("start", date);
                      if (!touched.end && values.end) {
                        const newEnd = moment(date)
                          .add(values.end.getTime() - values.start.getTime(), "ms")
                          .toDate();
                        setFieldValue("end", newEnd);
                      }
                    }}
                    {...datePickerActionTexts}
                    renderInput={(params) => (
                      <TextField {...params} sx={{ width: 112 }} variant="standard" />
                    )}
                  />
                  <TimeSelect
                    label={translate("calendarPage.form.startTime")}
                    value={moment(values.start).format("HH:mm")}
                    onChange={(timeValue) => {
                      const [hours, minutes] = timeValue.split(":").map(Number);

                      const newDate = moment(values.start)
                        .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
                        .toDate();

                      setFieldValue("start", newDate);

                      if (!touched.end && values.end) {
                        const newEnd = moment(newDate)
                          .add(values.end.getTime() - values.start.getTime(), "ms")
                          .toDate();
                        setFieldValue("end", newEnd);
                      }
                    }}
                    sx={{ width: 140 }}
                    variant="standard"
                  />
                </Stack>
                <Stack sx={{ mb: 3 }} direction="row" gap={2}>
                  <MobileDatePicker
                    label={translate("calendarPage.form.endDay")}
                    minDate={new Date()}
                    inputFormat="dd/MM/yyyy"
                    value={values.end}
                    onChange={(date) => {
                      setFieldValue("end", date);
                    }}
                    {...datePickerActionTexts}
                    renderInput={(params) => (
                      <TextField {...params} sx={{ width: 112 }} variant="standard" />
                    )}
                  />
                  <TimeSelect
                    label={translate("calendarPage.form.endTime")}
                    value={moment(values.end).format("HH:mm")}
                    onChange={(timeValue) => {
                      const [hours, minutes] = timeValue.split(":").map(Number);
                      const newDate = moment(values.end)
                        .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
                        .toDate();
                      setFieldValue("end", newDate);
                    }}
                    sx={{ width: 140 }}
                    variant="standard"
                  />
                </Stack>
              </>
            )}
            <Stack gap={2}>
              <Stack gap={2}>
                <NewMultipleContactPicker
                  // A meeting's attendees are "invitees" everywhere in this form, the same
                  // noun the note below uses. A task keeps the picker's own label.
                  label={
                    isTaskEvent ? undefined : translate("calendarPage.form.attendeesLabelMeeting")
                  }
                  selectedContacts={parsedAttendeesToContacts}
                  setSelectedContacts={(newContacts) => setFieldValue("attendees", newContacts)}
                />
                {!!parsedAttendeesToContacts.length && (
                  <Stack gap={1}>
                    {parsedAttendeesToContacts.map((contact) => (
                      <CalendarCustomerCard open attendee={contact} key={contact.id} />
                    ))}
                  </Stack>
                )}
                {showClientNote && (
                  // Directly under the invitees it is sent to, with the listing picker
                  // between it and the internal note so the two are not mistaken for each
                  // other. Not `getFieldProps`: the value is normalised on the way in.
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    name="clientNote"
                    label={translate("calendarPage.form.clientNote")}
                    placeholder={translate("calendarPage.form.clientNotePlaceholder")}
                    value={values.clientNote ?? ""}
                    onChange={(e) =>
                      setFieldValue("clientNote", normalizeClientNoteInput(e.target.value))
                    }
                    onKeyDown={handleClientNoteKeyDown}
                    inputProps={{ maxLength: CALENDAR_CLIENT_NOTE_MAX_LENGTH }}
                    error={Boolean(errors.clientNote)}
                    helperText={
                      <Box component="span" sx={{ display: "flex", gap: 1.5 }}>
                        <Box component="span" sx={{ flexGrow: 1 }}>
                          {translate(
                            isCreating
                              ? "calendarPage.form.clientNoteHelper"
                              : "calendarPage.form.clientNoteHelperEdit"
                          )}
                        </Box>
                        <Box component="span" dir="ltr" sx={{ flexShrink: 0 }}>
                          {clientNoteLength}/{CALENDAR_CLIENT_NOTE_MAX_LENGTH}
                        </Box>
                      </Box>
                    }
                  />
                )}
              </Stack>
              <Stack gap={2}>
                <NewListingPicker
                  loading={isSubmitting}
                  selectedListings={selectedListings}
                  onSelectedListings={setSelectedListings}
                />
                {!!selectedListings.length && (
                  <Stack gap={1}>
                    {selectedListings.map((listing) => (
                      <CalendarListingCard listing={listing} key={listing.id} />
                    ))}
                  </Stack>
                )}
              </Stack>
              {!isTaskEvent && (
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  label={translate("calendarPage.form.description")}
                  {...getFieldProps("description")}
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                />
              )}
              <ColorSinglePicker {...getFieldProps("textColor")} colors={COLOR_OPTIONS} />
            </Stack>
          </DialogContent>
          {isCheckingConflicts && (
            <Box sx={{ px: 3, pb: 1 }}>
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                {translate("calendarPage.form.conflictCheckLoading")}
              </Alert>
            </Box>
          )}
          {!isCheckingConflicts && pastMeetingRealtors.length > 0 && (
            <Box sx={{ px: 3, pb: 1 }}>
              <Alert
                severity="info"
                sx={{
                  "& .MuiAlert-message": { display: "block", width: "100%" }
                }}
              >
                <AlertTitle>{translate("calendarPage.form.pastMeetingsTitle")}</AlertTitle>
                <Stack component="ul" sx={{ pl: 2, m: 0 }} gap={0.5}>
                  {pastMeetingRealtors.map((entry, index) => (
                    <Box component="li" key={`${entry.contactName}-${entry.realtorName}-${index}`}>
                      {translate("calendarPage.form.pastMeetingsEntry", {
                        contactName: entry.contactName,
                        realtorName: entry.realtorName,
                        count: entry.meetingsCount,
                        date: moment(entry.lastMeetingDate).format("DD/MM/YYYY")
                      })}
                    </Box>
                  ))}
                </Stack>
              </Alert>
            </Box>
          )}
          {!isCheckingConflicts && (conflictWarnings.length > 0 || conflictCheckFailed) && (
            <Box sx={{ px: 3, pb: 1 }}>
              {conflictCheckFailed && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1 }}
                  action={
                    <Button color="inherit" size="small" onClick={fetchConflicts}>
                      {translate("calendarPage.form.conflictCheckRetry")}
                    </Button>
                  }
                >
                  {translate("calendarPage.form.conflictCheckFailed")}
                </Alert>
              )}
              {conflictWarnings.map((warning, index) => (
                <Alert severity="warning" key={index} sx={{ mb: 1 }}>
                  {warning.eventType === "meeting"
                    ? translate("calendarPage.form.conflictWarningMeeting", {
                        contactName: warning.contactName,
                        realtorName: warning.realtorName,
                        date: moment(warning.date).format("DD/MM/YYYY")
                      })
                    : translate("calendarPage.form.conflictWarningTask", {
                        contactName: warning.contactName,
                        realtorName: warning.realtorName,
                        date: moment(warning.date).format("DD/MM/YYYY")
                      })}
                </Alert>
              ))}
            </Box>
          )}
          <DialogActions>
            {!isCreating && (
              <Tooltip title="Delete Event">
                <IconButton onClick={handleDelete}>
                  <Icon icon={trash2Fill} width={20} height={20} />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button type="button" variant="outlined" color="inherit" onClick={onCancel}>
              {translate("general.cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              loading={isSubmitting}
              disabled={isDateError || Object.keys(errors).length > 0 || isCheckingConflicts}
              onClick={submitForm}
            >
              {isCreating
                ? translate("calendarPage.control.add")
                : translate("calendarPage.control.edit")}
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </>
  );
}
